import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, Clock, Flame, ArrowUpRight, ArrowDownRight, Layers } from 'lucide-react';
import baseapi from '../../baseapi';
import { useTheme } from '../../context/ThemeContext';

/**
 * Devotees for Darshan - peak trends, in vs out (Overview, 14 Oct 2026).
 *
 * In  = people entering the Antharalayam through the four darshan doors (100 / 300 / 500 Rs and the free line)
 * Out = people leaving through the two Antharalayam exits (192.168.2.44 and .125)
 */
const DOORS = ['CP-TEMPLE-ANTARALAYAM-ENTRY-3', 'CP-ANTHARALAYAM-ENTRY', 'CP-TEMPLE-ANTARALAYAM-ENTRY-2', 'CP-VIEW-2-190'];
const EXITS = ['CP-TEMPLE-ANTARALAYAM-EXIT', 'CP-VIEW-2-125'];
const WINDOWS = [
  { key: 'today', label: 'Today', minutes: 0, bucket: 15 },
  { key: '15', label: '15 min', minutes: 15, bucket: 1 },
  { key: '30', label: '30 min', minutes: 30, bucket: 1 },
  { key: '60', label: '1 hour', minutes: 60, bucket: 1 },
  { key: '120', label: '2 hours', minutes: 120, bucket: 2 },
] as const;
type WindowKey = typeof WINDOWS[number]['key'];
interface SeriesPoint { t: number; toward: number; away: number }
interface Minute { inn: number; out: number; any: number; outAny: number }
interface Official { inn: number | null; out: number | null; inside: number | null }
interface Placement { periods: { from: number; to: number; amount: number }[]; unplaced: number; fill: Record<string, number> }

const IST = 19800;
const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
const fmt = (v: number) => Math.round(v).toLocaleString('en-IN');
// people per minute: always whole people, never "2.1" or "0.5" (14 Oct 2026)
const rate = (v: number) => Math.round(v).toLocaleString('en-IN');
// whole people for the "crowd inside" sentence
const people = (v: number) => Math.max(1, Math.round(v)).toLocaleString('en-IN');
const dur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);
// temple palette (14 Oct 2026): In = saffron, Out = kumkum crimson, busiest hour = gold, darshan closed = steel blue
// #4D8DBA (the only cool colour, so "closed" never reads as data). In / Out checked to stay apart in both themes,
// also for colour-blind viewers; the dark theme uses brighter steps of the same hues.
const IN_COLOR = '#E6781B';
const IN_COLOR_DARK = '#F08A2C';
// const OUT_COLOR = '#BE123C';
// const OUT_COLOR_DARK = '#F43F5E';
const PEAK_HOUR = { light: '#E6781B', dark: '#F08A2C' };   // orange (matching first color) instead of brown
const CLOSED = { light: { hatch: '#4D8DBA', edge: '#4D8DBA', label: '#336A91' }, dark: { hatch: '#4D8DBA', edge: '#6FA6CC', label: '#A3C8E2' } };
const PAUSE_MAX = 5, PAUSE_MIN = 10, STARTED = 20;
interface Pause { start: number; end: number; minutes: number; ongoing: boolean; outMoving: boolean }

async function series(cp: string): Promise<SeriesPoint[]> {
  const r = await baseapi.get(`checkpoints/${encodeURIComponent(cp)}/series?minutes=1440`);
  return (r.data?.points || []) as SeriesPoint[];
}
const busier = (points: SeriesPoint[]): 'toward' | 'away' =>
  points.reduce((a, p) => a + (p.away || 0), 0) > points.reduce((a, p) => a + (p.toward || 0), 0) ? 'away' : 'toward';

// Modern Glassmorphic Tooltip with clean typography
function CustomTooltip({ active, payload, bucket }: any) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  const inVal = data?.inn ?? 0;                // the real count of the piece, not the drawn (scaled) value
  // const outVal = payload.find((p: any) => p.dataKey === 'out')?.value ?? 0;
  const mins = data?.minutes || bucket || 1;

  return (
    <div className="bg-white/95 dark:bg-[#161822]/95 backdrop-blur-md p-2.5 sm:p-3 rounded-xl border border-neutral-200 dark:border-neutral-700/70 shadow-xl text-xs space-y-1.5 min-w-[190px]">
      <div className="font-bold text-neutral-800 dark:text-neutral-100 border-b border-neutral-100 dark:border-neutral-800 pb-1.5 flex items-center justify-between">
        <span>{mins > 1 ? `${hm(data.t)} – ${hm(data.t + mins * 60)}` : hm(data.t)}{data?.closed ? ' · Darshan closed' : ''}</span>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
          {mins} min
        </span>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3 text-[#C2620F] dark:text-orange-400">
          <span className="flex items-center gap-1.5 font-medium">
            <span className="w-2 h-2 rounded-full bg-[#E6781B] dark:bg-[#F08A2C] shadow-xs" />
            Entered:
          </span>
          <span className="font-bold text-neutral-900 dark:text-white tabular-nums">
            {fmt(inVal)} {mins > 1 ? <span className="text-[10.5px] text-neutral-600 dark:text-neutral-300 font-semibold">({rate(inVal / mins)} per min)</span> : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export interface DevoteeTrendMetrics {
  peak: { label: string; t: number; inn: number; minutes: number } | null;
  nowIn: number;
  nowOut: number;
  peakHour: [number, number] | null;
  bucket: number;
  minutes: number;
  winLabel?: string;
}

export function DevoteeTrend({
  hideCards = true,
  onMetricsChange,
}: {
  hideCards?: boolean;
  onMetricsChange?: (m: DevoteeTrendMetrics) => void;
} = {}) {
  const { theme } = useTheme();
  const light = theme === 'light';
  const [win, setWin] = useState<WindowKey>(() => {
    try { return (localStorage.getItem('devotee_trend_window') as WindowKey) || 'today'; } catch { return 'today'; }
  });
  const [perMinute, setPerMinute] = useState<Map<number, Minute> | null>(null);
  const [official, setOfficial] = useState<Official>({ inn: null, out: null, inside: null });
  const [placement, setPlacement] = useState<Placement>({ periods: [], unplaced: 0, fill: {} });
  const [updated, setUpdated] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const scrolledTodayRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [doors, exits, ov, adj] = await Promise.all([
        Promise.all(DOORS.map(series)),
        Promise.all(EXITS.map(series)),
        baseapi.get('overview').then(r => r.data?.kpis || {}).catch(() => ({})),
        baseapi.get('dashboard/adjust/periods').then(r => r.data || {}).catch(() => ({})),
      ]);
      setPlacement({ periods: adj.periods || [], unplaced: adj.unplaced || 0, fill: adj.fill_minutes || {} });
      setOfficial({ inn: ov.total ?? null, out: ov.darshan ?? ov.exited ?? null, inside: ov.antharalayam ?? null });
      const m = new Map<number, Minute>();
      const at = (t: number) => { let x = m.get(t); if (!x) { x = { inn: 0, out: 0, any: 0, outAny: 0 }; m.set(t, x); } return x; };
      doors.forEach(points => { const d = busier(points); points.forEach(p => { const x = at(p.t); x.inn += p[d] || 0; x.any += (p.toward || 0) + (p.away || 0); }); });
      exits.forEach(points => {
        const d = busier(points), o = d === 'toward' ? 'away' : 'toward';
        points.forEach(p => { const x = at(p.t); x.out += (p[d] || 0) - (p[o] || 0); x.outAny += (p.toward || 0) + (p.away || 0); });
      });
      setPerMinute(m);
      setUpdated(Date.now());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem('devotee_trend_window', win); } catch { /* not remembered */ }
  }, [win]);

  const w = WINDOWS.find(x => x.key === win) || WINDOWS[0];

  const view = useMemo(() => {
    if (!perMinute || perMinute.size === 0) return null;
    const keys = [...perMinute.keys()];
    const end = Math.max(Math.max(...keys), Math.floor(Date.now() / 60_000) * 60 - 60);
    const dayStart = Math.min(...keys);

    const outFilled = new Map<number, number>();
    {
      let runStart: number | null = null, runIn = 0;
      const flush = (until: number) => {
        if (runStart !== null && (until - runStart) / 60 >= 10 && runIn >= 50)
          for (let mt = runStart; mt < until; mt += 60) outFilled.set(mt, perMinute.get(mt)?.inn || 0);
        runStart = null; runIn = 0;
      };
      for (let mt = dayStart; mt <= end; mt += 60) {
        const x = perMinute.get(mt);
        if (!(x?.outAny)) { if (runStart === null) runStart = mt; runIn += x?.inn || 0; } else flush(mt);
      }
      flush(end + 60);
    }

    const rawOut = (mt: number) => outFilled.has(mt) ? outFilled.get(mt)! : Math.max(0, perMinute.get(mt)?.out || 0);
    const counted = (mt: number) => { const x = perMinute.get(mt); return !!(x && (x.any || x.outAny)); };
    const weight = new Map<number, number>();
    for (let mt = dayStart; mt <= end; mt += 60) {
      if (counted(mt)) { weight.set(mt, perMinute.get(mt)?.inn || 0); continue; }
      let sum = 0, n = 0;
      for (let d = 60; d <= 30 * 60; d += 60) for (const nb of [mt - d, mt + d]) if (nb >= dayStart && nb <= end && counted(nb)) { sum += perMinute.get(nb)?.inn || 0; n++; }
      weight.set(mt, n ? sum / n : 0);
    }

    let opened = Infinity;
    for (let mt = dayStart; mt <= end; mt += 60) if ((perMinute.get(mt)?.inn || 0) >= STARTED) { opened = mt; break; }
    while (isFinite(opened) && opened - 60 >= dayStart && (perMinute.get(opened - 60)?.inn || 0) > PAUSE_MAX) opened -= 60;
    const morningClosed = isFinite(opened) && (opened - dayStart) / 60 >= PAUSE_MIN;
    if (morningClosed) for (let mt = dayStart; mt < opened; mt += 60) weight.set(mt, 0);

    const extra = new Map<number, number>();
    const place = (from: number, to: number, amount: number) => {
      const mins: number[] = [];
      for (let mt = Math.max(dayStart, Math.floor(from / 60) * 60); mt < Math.min(to, end + 60); mt += 60) mins.push(mt);
      if (!mins.length || !amount) return;
      const tw = mins.reduce((a2, mt) => a2 + (weight.get(mt) || 0), 0);
      mins.forEach(mt => extra.set(mt, (extra.get(mt) || 0) + amount * (tw ? (weight.get(mt) || 0) / tw : 1 / mins.length)));
    };

    placement.periods.forEach(pz => place(pz.from, pz.to, pz.amount));
    if (placement.unplaced) place(dayStart, end + 60, placement.unplaced);
    Object.entries(placement.fill).forEach(([t, v]) => { const mt = Number(t); extra.set(mt, (extra.get(mt) || 0) + v); });

    const IN = (mt: number) => Math.max(0, (perMinute.get(mt)?.inn || 0) + (extra.get(mt) || 0));

    let outDay = 0, completedTarget = official.out;
    for (let mt = dayStart; mt <= end; mt += 60) outDay += rawOut(mt) + (extra.get(mt) || 0);
    const outScale = completedTarget && outDay > 0 ? completedTarget / outDay : 1;
    const OUT = (mt: number) => Math.max(0, (rawOut(mt) + (extra.get(mt) || 0)) * outScale);

    const first = w.minutes ? end - (w.minutes - 1) * 60 : Math.min(...keys);
    const step = w.bucket * 60;
    const start = Math.floor((first + IST) / step) * step - IST;
    const bars: { t: number; x: number; label: string; inn: number; out: number; minutes: number }[] = [];

    for (let t = start; t <= end; t += step) {
      let inn = 0, out = 0, mins = 0;
      for (let mt = t; mt < t + step && mt <= end; mt += 60) {
        if (mt < first) continue;
        inn += IN(mt); out += OUT(mt); mins++;
      }
      if (mins) bars.push({ t, x: t + mins * 30, label: hm(t), inn: Math.round(inn), out: Math.round(out), minutes: mins });
    }

    const minutes = bars.reduce((a, b) => a + b.minutes, 0) || 1;
    let totIn = 0, totOut = 0;
    let camIn = 0, camOut = 0;
    for (let mt = first; mt <= end; mt += 60) { totIn += IN(mt); totOut += OUT(mt); camIn += perMinute.get(mt)?.inn || 0; camOut += Math.max(0, perMinute.get(mt)?.out || 0); }
    if (!w.minutes && official.inn != null) totIn = official.inn;
    if (!w.minutes && official.out != null) totOut = official.out;
    const peak = bars.reduce((p, b) => (b.inn / b.minutes > p.inn / p.minutes ? b : p), bars[0]);
    let nowIn = 0, nowOut = 0;
    for (let mt = end - 4 * 60; mt <= end; mt += 60) { nowIn += IN(mt); nowOut += OUT(mt); }

    // peak hour = the busiest 60 minutes in a row, not a clock hour (15 Oct 2026: the busiest clock hour 04:00-05:00
    // did not contain the busiest 15 minutes 05:30-05:45, because a darshan stop pulled 05:00-06:00 down - it read as
    // a contradiction). A sliding window always contains the peak 15 minutes.
    let peakHour: [number, number] | null = null;
    {
      let sum = 0;
      for (let mt = dayStart; mt <= end; mt += 60) {
        sum += IN(mt);
        if (mt - 3600 >= dayStart) sum -= IN(mt - 3600);
        const start = Math.max(dayStart, mt - 3540);
        if (mt - dayStart >= 3540 && (!peakHour || sum > peakHour[1])) peakHour = [start, Math.round(sum)];
      }
    }

    const pauses: Pause[] = morningClosed
      ? [{ start: dayStart, end: opened - 60, minutes: (opened - dayStart) / 60, ongoing: false, outMoving: false }] : [];
    const firstFlow = keys.filter(t => (perMinute.get(t)?.inn || 0) >= STARTED).reduce((a, t) => Math.min(a, t), Infinity);
    let run: { s: number; e: number; out: number; any: number } | null = null;
    const close = (ongoing: boolean) => {
      if (run && (run.e - run.s) / 60 + 1 >= PAUSE_MIN && run.out > 0 && run.any > 0)
        pauses.push({ start: run.s, end: run.e, minutes: (run.e - run.s) / 60 + 1, ongoing, outMoving: run.out > 0 });
      run = null;
    };
    for (let mt = isFinite(firstFlow) ? firstFlow : end + 60; mt <= end; mt += 60) {
      const x = perMinute.get(mt);
      // what the bars show (camera count + corrections placed in time): a gap filled in for cameras that were down
      // (16 Oct 2026: power cut 05:55-06:45, 3,400 placed) is not a darshan closure
      if (IN(mt) <= PAUSE_MAX) {
        if (!run) run = { s: mt, e: mt, out: 0, any: 0 };
        run.e = mt; run.out += Math.abs(x?.out || 0); run.any += x?.any || 0;
      } else close(false);
    }
    close(true);

    // The time axis is real clock time (14 Oct 2026). It used to be one slot per bucket, which snapped a closure to its
    // 15-minute slots: 10:18-10:30 fell inside the single 10:15 slot and was drawn zero pixels wide.
    const domain: [number, number] = [bars[0]?.t ?? end, end + 60];
    const span = (domain[1] - domain[0]) / 60;
    const tickStep = (span <= 45 ? 5 : span <= 75 ? 10 : span <= 150 ? 15 : span <= 360 ? 30 : 60) * 60;
    const ticks: number[] = [];
    for (let t = Math.ceil((domain[0] + IST) / tickStep) * tickStep - IST; t <= domain[1]; t += tickStep) ticks.push(t);
    if (!ticks.length || domain[1] - ticks[ticks.length - 1] > tickStep / 2) ticks.push(domain[1]);
    const shaded = pauses
      .map(pz => ({ ...pz, x1: Math.max(pz.start, domain[0]), x2: Math.min(pz.end + 60, domain[1]) }))
      .filter(pz => pz.x2 > pz.x1);

    // Use the source series' one-minute resolution for Today so brief closures and flow changes are visible.
    // Short rolling windows retain their selected bucket size.
    const cstep = (w.key === 'today' ? 1 : w.bucket) * 60;
    const chart: { t: number; x: number; label: string; inn: number; out: number; minutes: number; plot: number; closed: boolean }[] = [];
    for (let t = Math.floor((domain[0] + IST) / cstep) * cstep - IST; t <= end; t += cstep) {
      let inn = 0, out = 0, mins = 0;
      for (let mt = t; mt < t + cstep && mt <= end; mt += 60) { if (mt < first) continue; inn += IN(mt); out += OUT(mt); mins++; }
      if (!mins) continue;
      const closed = shaded.some(pz => t >= pz.x1 && Math.min(t + cstep, end + 60) <= pz.x2);
      chart.push({ t, x: t + cstep / 2, label: hm(t), inn: Math.round(inn), out: Math.round(out), minutes: mins, plot: Math.round(inn), closed });
    }
    if (chart.length) domain[1] = Math.max(domain[1], chart[chart.length - 1].t + cstep);

    return {
      pauses, shaded, domain, ticks, chart, cstep,
      camIn, camOut, filledMinutes: outFilled.size,
      bars, totIn, totOut: Math.max(0, totOut), avgIn: totIn / minutes, avgOut: Math.max(0, totOut) / minutes,
      peak, nowIn: nowIn / 5, nowOut: Math.max(0, nowOut) / 5, peakHour: peakHour as [number, number] | null,
    };
  }, [perMinute, w, official, placement]);

  useEffect(() => {
    if (onMetricsChange && view) {
      onMetricsChange({
        peak: view.peak ? { label: view.peak.label, t: view.peak.t, inn: view.peak.inn, minutes: view.peak.minutes } : null,
        nowIn: view.nowIn,
        nowOut: view.nowOut,
        peakHour: view.peakHour,
        bucket: w.bucket,
        minutes: w.minutes,
        winLabel: WINDOWS.find(x => x.key === win)?.label,
      });
    }
  }, [view, w.bucket, w.minutes, win, onMetricsChange]);

  useEffect(() => {
    const el = chartScrollRef.current;
    if (win === 'today' && view?.chart.length && el && !scrolledTodayRef.current) {
      el.scrollLeft = el.scrollWidth;
      scrolledTodayRef.current = true;
    }
  }, [win, view?.chart.length]);

  const cmin = (view?.cstep ?? w.bucket * 60) / 60;      // the chart's bar length (5 min in Today; the cards use 15)
  const unit = cmin === 1 ? '1 minute' : `${cmin} minutes`;
  const axis = { fontSize: 11, fontWeight: 600, fill: light ? '#5F534B' : '#a3abbd' };
  const closed = light ? CLOSED.light : CLOSED.dark;
  const inColor = light ? IN_COLOR : IN_COLOR_DARK;

  return (
    <div className="p-3.5 sm:p-4 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs transition-colors">
      {/* Top Header & Range Filters */}
      <div className="flex flex-wrap items-center gap-3 justify-between pb-3 border-b border-neutral-100 dark:border-[#262c39]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-lg bg-orange-500/10 text-orange-500 dark:bg-orange-500/15">
              <Layers className="w-4 h-4 stroke-[2.5]" />
            </span>
            <h3 className="text-sm sm:text-base font-black text-neutral-900 dark:text-neutral-100 tracking-tight">
              Devotees Entering for Darshan
            </h3>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
            People entering and leaving the Antharalayam · {w.minutes ? `last ${WINDOWS.find(x => x.key === win)?.label}` : 'today since midnight'} · each bar = {unit}
            {updated ? ` · updated ${hm(updated / 1000)}` : ''}
          </p>
        </div>

        {/* Premium Segmented Tab Switcher */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-[#13151c] border border-neutral-200/80 dark:border-[#262c39] shadow-inner">
          {WINDOWS.map(x => {
            const isSelected = win === x.key;
            return (
              <button
                key={x.key}
                onClick={() => setWin(x.key)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-150 cursor-pointer ${isSelected
                  ? 'bg-white dark:bg-[#252a3a] text-orange-600 dark:text-orange-400 shadow-sm border border-neutral-200/60 dark:border-neutral-700/80 scale-[1.02]'
                  : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 hover:bg-white/50 dark:hover:bg-neutral-800/40'
                  }`}
              >
                {x.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3 Redesigned KPI Cards matching the Project Design Language */}
      {!hideCards && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
          {/* Card 1: Peak Darshan Entry */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-orange-500/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-orange-600 dark:text-orange-400">
                <Flame className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>{w.bucket === 1 ? 'Peak minute' : `Peak ${w.bucket} minutes`} – most devotees entered</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0">
                {w.minutes ? `Last ${WINDOWS.find(x => x.key === win)?.label}` : 'Today'}
              </span>
            </div>

            <div className="mt-1 text-3xl sm:text-[42px] font-black text-neutral-900 dark:text-neutral-50 tracking-tight leading-tight">
              {view?.peak ? `${view.peak.label}${w.bucket > 1 ? `–${hm(view.peak.t + w.bucket * 60)}` : ''}` : '—'}
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              <span>{view?.peak
                ? <><strong className="text-neutral-900 dark:text-neutral-100 font-black text-sm">{fmt(view.peak.inn)}</strong> devotees entered{w.bucket > 1 ? <> · about <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{rate(view.peak.inn / view.peak.minutes)}</strong> every minute</> : ' in that minute'}</>
                : '—'}</span>
            </div>
          </div>

          {/* Card 2: Right Now */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-[#BE123C]/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                <Activity className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>In &amp; out of Antharalayam</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                Live
              </span>
            </div>

            <div className="mt-1 flex items-baseline gap-2.5">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-[42px] font-black text-[#E6781B] dark:text-[#F08A2C] tracking-tight leading-tight">
                  {view ? fmt(view.nowIn * 5) : '—'}
                </span>
                <span className="text-sm sm:text-base font-bold text-[#C2620F] dark:text-orange-400">went in</span>
              </div>
              <span className="text-neutral-300 dark:text-neutral-700 text-lg">·</span>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-[42px] font-black text-[#BE123C] dark:text-[#F43F5E] tracking-tight leading-tight">
                  {view ? fmt(view.nowOut * 5) : '—'}
                </span>
                <span className="text-sm sm:text-base font-bold text-[#BE123C]/80 dark:text-[#F43F5E]">came out</span>
              </div>
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              {view ? (
                Math.abs(view.nowIn - view.nowOut) * 5 < 5 ? (
                  <span className="text-neutral-700 dark:text-neutral-200 font-bold">Crowd inside is steady – about as many came out as went in</span>
                ) : view.nowIn > view.nowOut ? (
                  <span className="text-[#C2620F] dark:text-amber-400 font-bold flex items-center gap-0.5">
                    <ArrowUpRight className="w-3.5 h-3.5 stroke-[2.5]" /> Crowd inside is growing – {people((view.nowIn - view.nowOut) * 5)} more people inside than 5 min ago
                  </span>
                ) : (
                  <span className="text-[#BE123C] dark:text-[#F43F5E] font-bold flex items-center gap-0.5">
                    <ArrowDownRight className="w-3.5 h-3.5 stroke-[2.5]" /> Crowd inside is going down – {people((view.nowOut - view.nowIn) * 5)} fewer people inside than 5 min ago
                  </span>
                )
              ) : (
                <span className="text-neutral-600 dark:text-neutral-400">Calculating…</span>
              )}
            </div>
          </div>

          {/* Card 3: Busiest Hour */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-orange-500/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-orange-600 dark:text-orange-400">
                <Clock className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>Peak hour – most devotees entered</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0">
                Today
              </span>
            </div>

            <div className="mt-1 text-3xl sm:text-[42px] font-black text-neutral-900 dark:text-neutral-50 tracking-tight leading-tight">
              {view?.peakHour ? `${hm(view.peakHour[0])}–${hm(view.peakHour[0] + 3600)}` : '—'}
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              <span>{view?.peakHour ? <><strong className="text-neutral-900 dark:text-neutral-100 font-black text-sm">{fmt(view.peakHour[1])}</strong> devotees entered · about <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{rate(view.peakHour[1] / 60)}</strong> every minute</> : '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Legend Header */}
      <div className="mt-4 flex items-center justify-between px-1 text-xs">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 font-bold text-neutral-800 dark:text-neutral-200">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E6781B] dark:bg-[#F08A2C] shadow-xs" />
            <span>Entering (darshan doors)</span>
          </div>
          {/* {view?.peakHour && (
            <div className="flex items-center gap-1.5 font-bold text-neutral-800 dark:text-neutral-200">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#E6781B] dark:bg-[#F08A2C]" />
              <span>Busiest hour {hm(view.peakHour[0])}–{hm(view.peakHour[0] + 3600)} · {fmt(view.peakHour[1])} devotees</span>
            </div>
          )} */}
        </div>
      </div>

      {/* Chart container (bars) */}
      {/* taller when there is a closure, for the "Darshan Closed" boxes in the strip above the plot */}
      <div ref={chartScrollRef} className="mt-2 w-full overflow-x-auto overscroll-x-contain">
      <div className={`w-full ${view?.shaded.length ? 'h-[260px] sm:h-[290px]' : 'h-[220px] sm:h-[250px]'}`}
        style={win === 'today' && view?.chart.length ? { width: `max(100%, ${view.chart.length * 12}px)` } : undefined}>
        {view && view.bars.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={view.chart} barCategoryGap={1} margin={{ top: view.shaded.length ? 58 : 18, right: 16, left: 0, bottom: 0 }}>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={light ? '#f1f5f9' : '#222735'}
              />

              <XAxis
                dataKey="x"
                type="number"
                domain={view.domain}
                ticks={view.ticks}
                tickFormatter={(t: number) => hm(t)}
                tick={axis}
                tickLine={false}
                axisLine={{ stroke: light ? '#e2e8f0' : '#272d3d' }}
                interval="preserveStartEnd"
                minTickGap={24}
                allowDataOverflow
              />

              <YAxis
                tick={axis}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={52}
                tickFormatter={(v: number) => fmt(v)}
              />

              <Tooltip
                cursor={{
                  stroke: light ? '#cbd5e1' : '#475569',
                  strokeWidth: 1.5,
                  strokeDasharray: '3 3',
                }}
                content={<CustomTooltip bucket={w.bucket} />}
              />

              {view.shaded.map(pz => (
                <ReferenceArea
                  key={pz.start}
                  x1={pz.x1}
                  x2={pz.x2}
                  ifOverflow="extendDomain"
                  fill={closed.hatch}
                  fillOpacity={light ? 0.22 : 0.3}
                  stroke={closed.edge}
                  strokeOpacity={0.75}
                  strokeDasharray="4 3"
                  // "Darshan Closed" and the time on a solid box in a strip above the plot, centred over its band (15 Oct
                  // 2026: a short closure in the Today view is a narrow band and its small label could hardly be read;
                  // inside the plot the box hid the line). A closure still going on is labelled left of its band's end
                  label={({ viewBox }: any) => {
                    if (!viewBox) return null;
                    const w = 124, h = 40;
                    const pzSpan = Math.max(1, pz.x2 - pz.x1);
                    const domainSpan = Math.max(1, (view.domain[1] || pz.x2) - (view.domain[0] || pz.x1));
                    const pxPerSec = viewBox.width > 0 ? viewBox.width / pzSpan : 0;
                    const plotLeft = pxPerSec > 0 ? viewBox.x - (pz.x1 - view.domain[0]) * pxPerSec : viewBox.x;
                    const plotRight = pxPerSec > 0 ? plotLeft + domainSpan * pxPerSec : viewBox.x + viewBox.width;

                    const minX = Math.max(4, plotLeft + 2);
                    const maxX = Math.max(minX, plotRight - w - 2);

                    const targetX = pz.ongoing
                      ? viewBox.x + viewBox.width - w
                      : viewBox.x + viewBox.width / 2 - w / 2;

                    const x = Math.min(maxX, Math.max(minX, targetX));
                    const y = viewBox.y - h - 16;
                    return (
                      <g>
                        <rect x={x} y={y} width={w} height={h} rx={8} fill={light ? '#ffffff' : '#181b23'} stroke={closed.edge} strokeWidth={1.5} />
                        <text x={x + w / 2} y={y + 16} textAnchor="middle" fontSize={13} fontWeight={800} fill={closed.label}>
                          <tspan x={x + w / 2}>Darshan Closed</tspan>
                          <tspan x={x + w / 2} dy={16} fontSize={13.5} fontWeight={900}>{hm(pz.start)}–{pz.ongoing ? 'now' : hm(pz.end + 60)}</tspan>
                        </text>
                      </g>
                    );
                  }}
                />
              ))}

              {/* peak entry: a line and a dot on the busiest bucket */}
              {/* {view.peak && (
                <ReferenceLine x={view.peak.x} stroke={inColor} strokeDasharray="3 3" strokeOpacity={0.9}
                  // grows inward near the chart's edges ("Busiest 140 p" was cut off when the peak was the latest point)
                  label={({ viewBox }: any) => {
                    if (!viewBox) return null;
                    const f = (view.peak.x - view.domain[0]) / Math.max(1, view.domain[1] - view.domain[0]);
                    return (
                      <text x={viewBox.x} y={viewBox.y - 6} textAnchor={f > 0.88 ? 'end' : f < 0.12 ? 'start' : 'middle'}
                        fontSize={10.5} fontWeight={800} fill={light ? '#C2620F' : '#fdba74'}>
                        Busiest {rate(view.peak.inn / view.peak.minutes)} per min
                      </text>
                    );
                  }} />
              )} */}

              {/* Today uses one bar per minute; rolling windows use their selected bucket size. */}
              {/* the busiest hour's bars in the page's maroon (pale gold in dark): 15 Oct 2026, a gold strip under the
                  saffron bars could not be told apart */}
              <Bar dataKey="plot" name="In (darshan doors)" fill={inColor} barSize={win === 'today' ? 11 : undefined} radius={[2, 2, 0, 0]}
                activeBar={{ fill: light ? '#C2620F' : '#fdba74' }} isAnimationActive={false}>
                {view.chart.map(b => {
                  const ph = view.peakHour;
                  const busy = !!ph && b.x >= ph[0] && b.x < ph[0] + 3600;
                  return <Cell key={b.t} fill={busy ? (light ? PEAK_HOUR.light : PEAK_HOUR.dark) : inColor} />;
                })}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-neutral-600 dark:text-neutral-400">
            {failed ? 'Counts not available right now. Retrying…' : 'Loading data…'}
          </div>
        )}
      </div>
      </div>

      {/* Darshan Closed Badges Footer */}
      {view?.pauses.length ? (
        <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-[#222735] flex flex-wrap items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-neutral-700 dark:text-neutral-300">
            <span className={`w-2 h-2 rounded-full bg-[#4D8DBA] ${view.pauses.some(pz => pz.ongoing) ? 'animate-pulse' : ''}`} />
            <span>Darshan Closed Timings:</span>
          </div>
          {view.pauses.map(pz => (
            <span
              key={pz.start}
              title="Nobody entered through the darshan doors for 10 minutes or more while people kept leaving: darshan was closed"
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#4D8DBA]/15 dark:bg-[#4D8DBA]/20 border border-[#4D8DBA]/50 dark:border-[#4D8DBA]/40 text-[#336A91] dark:text-[#A3C8E2] shadow-2xs"
            >
              <Clock className="w-3 h-3 text-[#4D8DBA] dark:text-[#6FA6CC]" />
              <span>{hm(pz.start)}–{pz.ongoing ? 'now' : hm(pz.end + 60)}</span>
              <span className="bg-[#4D8DBA]/20 dark:bg-[#4D8DBA]/30 text-[#2B5A7B] dark:text-[#CFE2F0] px-1.5 py-0.2 rounded-sm text-[10px] font-black">
                {dur(pz.minutes)}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
